import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconApps,
  IconCode,
  IconDatabase,
  IconInfoCircle,
  IconKey,
  IconLock,
  IconShield,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { PageHeader } from "../../../components/PageHeader";
import { useOrganizationLimits } from "../../../../../business/limits.hook";
import { LimitUsageBar } from "../../../components/LimitIndicator";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const IAMDocumentation = () => {
  const { classes } = useStyles();
  const { getLimit, loading: limitsLoading } = useOrganizationLimits([
    "ORGANIZATION_GROUPS",
    "ORGANIZATION_MEMBERS",
    "CUSTOM_IDENTITY_PROVIDERS",
  ]);
  const groupsLimit = getLimit("ORGANIZATION_GROUPS");
  const membersLimit = getLimit("ORGANIZATION_MEMBERS");
  const idpLimit = getLimit("CUSTOM_IDENTITY_PROVIDERS");

  const activeLimits = [groupsLimit, membersLimit, idpLimit].filter(Boolean);

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="IAM Documentation"
        subtitle="Complete reference for Identity and Access Management"
        showBreadcrumbs={false}
      />

      {!limitsLoading && activeLimits.length > 0 && (
        <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
          {groupsLimit && <LimitUsageBar limit={groupsLimit} label="Groups" />}
          {membersLimit && (
            <LimitUsageBar limit={membersLimit} label="Members" />
          )}
          {idpLimit && (
            <LimitUsageBar limit={idpLimit} label="Identity Providers" />
          )}
        </SimpleGrid>
      )}

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#members">Members</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#groups">Groups & Permissions</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#applications">Applications (OAuth2)</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#permission-model">Permission Model</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconShield size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            IAM (Identity and Access Management) is the system that controls who
            can access your organization and what they are allowed to do. It
            manages authentication (who you are) and authorization (what you can
            do) for both human users and machine clients.
          </Text>
          <Text mb="md">IAM is composed of three interconnected entities:</Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Organization
  +-- Members[] (human users with console or platform access)
  |    +-- Group (assigned permission group)
  |    +-- Account (linked user account with email & profiles)
  |
  +-- Groups[] (named collections of permissions)
  |    +-- Permissions[] (permission ID + resource patterns)
  |
  +-- Applications[] (OAuth2 clients for server-to-server access)
       +-- Attached Apps[] (apps linked to this organization)
            +-- Group (assigned permission group)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Every member and application is assigned to exactly one group.
            Groups define what permissions the member or application has within
            the organization. Some groups are system-managed and cannot be
            deleted.
          </Alert>
        </div>

        {/* ===================== MEMBERS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsers size={18} />}
            title="Members"
            id="members"
          />
          <Text mb="md">
            Members are users that have access to your organization. They can be
            invited in two ways: by email (for console access) or by player
            account (for platform access).
          </Text>

          <Title order={4} mb="xs">
            Invitation Methods
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Invite by Email
              </Text>
              <Text size="sm">
                Inviting a member by email grants them access to the WellPlayed
                Console. They will receive an invitation and can log in using
                their email credentials. Use this for administrators and staff
                who need to manage the organization.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Invite by Player Account
              </Text>
              <Text size="sm">
                Inviting a member by their player username links their platform
                player profile to the organization. Use the player search
                autocomplete to find and add them. This is used for granting
                platform-level permissions to specific players.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Member Management
          </Title>
          <Accordion variant="separated" mb="md">
            <Accordion.Item value="add-member">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsers size={16} />
                  <Text fw={500}>Adding a Member</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Navigate to <Code>/iam/members/new</Code>. Fields:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Email or Player</strong> (required) - Either an email
                    address or a player username search
                  </List.Item>
                  <List.Item>
                    <strong>Group</strong> (optional) - Select a permission group
                    from the dropdown (fetched from{" "}
                    <Code>organizationGroups</Code>). A member can be created
                    without a group, in which case <Code>groupId</Code> and{" "}
                    <Code>group</Code> are <Code>null</Code> until a group is
                    assigned.
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  Uses the <Code>setOrganizationMembership</Code> mutation with
                  either <Code>email</Code> or <Code>userId</Code>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="update-member">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsersGroup size={16} />
                  <Text fw={500}>Updating a Member's Group</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Click a member in the list to edit their group assignment. The
                  same <Code>setOrganizationMembership</Code> mutation is used
                  (it acts as an upsert). You can change which group the member
                  belongs to, effectively changing their permissions.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="remove-member">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsers size={16} />
                  <Text fw={500}>Removing a Member</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Use the delete action on the members list. This calls{" "}
                  <Code>deleteOrganizationMembership</Code> with the user's ID.
                  The user will immediately lose access to the organization.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== GROUPS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsersGroup size={18} />}
            title="Groups & Permissions"
            id="groups"
          />
          <Text mb="md">
            Groups are named collections of permissions. Each member and
            attached application is assigned to exactly one group. Groups define
            the access level for all entities assigned to them.
          </Text>

          <Title order={4} mb="xs">
            System Groups
          </Title>
          <Text mb="sm" size="sm">
            These groups are automatically created and cannot be deleted:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="red">ADMIN</Badge>
              </Group>
              <Text size="sm">
                Full permissions on all resources. Automatically granted to the
                organization creator. Cannot be deleted or have its permissions
                modified.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="blue">CONNECTED</Badge>
              </Group>
              <Text size="sm">
                Automatically assigned to any authenticated user interacting
                with the organization's platform. Defines baseline permissions
                for logged-in users.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="gray">ANONYMOUS</Badge>
              </Group>
              <Text size="sm">
                Automatically assigned to unauthenticated visitors. Defines what
                public, non-logged-in users can see and do on the platform.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Custom Groups
          </Title>
          <Text mb="sm">
            You can create custom groups with specific permission sets for
            different roles (e.g. "Moderator", "Tournament Admin", "Content
            Editor").
          </Text>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="create-group">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsersGroup size={16} />
                  <Text fw={500}>Creating a Group</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Navigate to <Code>/iam/groups/new</Code>. Required fields:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Name</strong> - Display name for the group
                  </List.Item>
                  <List.Item>
                    <strong>Permissions</strong> - Select from the available
                    permissions list (fetched from{" "}
                    <Code>availablePermissions</Code>). Each permission can
                    optionally be scoped to specific resources.
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  Uses the <Code>createOrganizationGroup</Code> mutation.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="permission-assignment">
              <Accordion.Control>
                <Group gap="xs">
                  <IconKey size={16} />
                  <Text fw={500}>Permission Assignment</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Each permission in a group consists of:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Permission ID</strong> - The permission identifier
                    (e.g. <Code>tournament:read</Code>,{" "}
                    <Code>player:write</Code>)
                  </List.Item>
                  <List.Item>
                    <strong>Resources</strong> - Array of resource patterns the
                    permission applies to. Supports wildcards (e.g.{" "}
                    <Code>*</Code> for all resources, or specific IDs)
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  Available permissions and their resource types are fetched
                  from the <Code>availablePermissions</Code> query which returns
                  the full list with descriptions and available variables.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="edit-group">
              <Accordion.Control>
                <Group gap="xs">
                  <IconUsersGroup size={16} />
                  <Text fw={500}>Editing & Deleting Groups</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Click a group in the list to edit its name and permissions.
                  Uses the <Code>updateOrganizationGroup</Code> mutation.
                </Text>
                <Alert
                  icon={<IconAlertCircle />}
                  color="yellow"
                  variant="light"
                  mt="xs"
                >
                  System groups (ADMIN, CONNECTED, ANONYMOUS) cannot be deleted.
                  Custom groups can only be deleted if no members or
                  applications are currently assigned to them.
                </Alert>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== APPLICATIONS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconApps size={18} />}
            title="Applications (OAuth2)"
            id="applications"
          />
          <Text mb="md">
            Applications are OAuth2 clients that use the Client Credentials Flow
            to access resources on behalf of your organization. They enable
            server-to-server integrations without requiring a human user
            session.
          </Text>

          <Title order={4} mb="xs">
            Application Lifecycle
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`1. Create App -----> 2. Attach to Org -----> 3. Assign Group -----> App can access resources
     |                    |                       |
     Set name,            Link the app            Permissions are
     URLs, config         to your org             inherited from group`}
            </GqlBlock>
          </Card>

          <Text mb="md">
            Applications have two layers: the app definition itself (shared
            across organizations) and the attachment to a specific organization
            with a group assignment.
          </Text>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="create-app">
              <Accordion.Control>
                <Group gap="xs">
                  <IconApps size={16} />
                  <Text fw={500}>Creating an Application</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Applications are created with the following configuration:
                </Text>
                <Table striped>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Code>name</Code>
                      </td>
                      <td>String!</td>
                      <td>Application display name</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>description</Code>
                      </td>
                      <td>String!</td>
                      <td>Application description</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>consentUrl</Code>
                      </td>
                      <td>String!</td>
                      <td>OAuth2 consent page URL</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>loginUrl</Code>
                      </td>
                      <td>String!</td>
                      <td>OAuth2 login page URL</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>redirectUrls</Code>
                      </td>
                      <td>[String!]!</td>
                      <td>Allowed OAuth2 redirect URIs</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>logoutRedirectUrls</Code>
                      </td>
                      <td>[String!]!</td>
                      <td>Allowed post-logout redirect URIs</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>requiresConsent</Code>
                      </td>
                      <td>Boolean!</td>
                      <td>Whether to show consent screen to users</td>
                    </tr>
                    <tr>
                      <td>
                        <Code>public</Code>
                      </td>
                      <td>Boolean</td>
                      <td>Whether the app is public (visible to other orgs)</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="attach-app">
              <Accordion.Control>
                <Group gap="xs">
                  <IconLock size={16} />
                  <Text fw={500}>Attaching an Application</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  To give an application access to your organization, attach it
                  using <Code>setOrganizationAttachedApp</Code>. You must
                  specify:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>App ID</strong> - The application to attach
                  </List.Item>
                  <List.Item>
                    <strong>Group</strong> - The permission group to assign
                    (determines what the app can access)
                  </List.Item>
                </List>
                <Text size="sm" mt="xs">
                  The attached app inherits all permissions from its assigned
                  group, just like a member would.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="secret-management">
              <Accordion.Control>
                <Group gap="xs">
                  <IconKey size={16} />
                  <Text fw={500}>Secret Management</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Each application has a client secret used for the Client
                  Credentials Flow:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    The secret is shown once when the app is created
                  </List.Item>
                  <List.Item>
                    Use <Code>resetOrganizationAppSecret</Code> to generate a
                    new secret (invalidates the old one)
                  </List.Item>
                  <List.Item>
                    Store secrets securely - they cannot be retrieved after
                    creation
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="detach-app">
              <Accordion.Control>
                <Group gap="xs">
                  <IconApps size={16} />
                  <Text fw={500}>Detaching & Deleting</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  <strong>Detach</strong>: Use{" "}
                  <Code>deleteOrganizationAttachedApp</Code> to remove an app's
                  access to your organization. The app itself still exists and
                  can be re-attached later.
                  <br />
                  <br />
                  <strong>Delete</strong>: Use{" "}
                  <Code>deleteOrganizationApp</Code> to permanently delete the
                  application. This also removes it from all organizations it
                  was attached to.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== PERMISSION MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconLock size={18} />}
            title="Permission Model"
            id="permission-model"
          />
          <Text mb="md">
            The permission system uses a role-based access control (RBAC) model
            with resource-level scoping. Permissions are always assigned through
            groups, never directly to members or applications.
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`Member/App ----> Group ----> Permissions[] ----> Resources[]
                              |                          |
                              Named role                 Each permission has:
                              (e.g. "Moderator")         - id (permission name)
                                                         - resources (scope patterns)`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Permission Structure
          </Title>
          <Text mb="sm">Each permission entry in a group consists of:</Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <Text>
                <Code>id</Code> - The permission identifier (e.g.{" "}
                <Code>tournament:manage</Code>, <Code>player:read</Code>).
                Available permissions are fetched from the{" "}
                <Code>availablePermissions</Code> query.
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Code>resources</Code> - An array of resource identifiers the
                permission applies to. Use <Code>*</Code> for all resources, or
                provide specific IDs to scope access to particular entities.
              </Text>
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Permission Check Flow
          </Title>
          <Text mb="sm">
            When checking if a user has permission to perform an action:
          </Text>
          <List type="ordered" spacing="sm" mb="md">
            <List.Item>
              <Text>
                <strong>Identify the user</strong> - From the OIDC token in the
                request
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Find their membership</strong> - Look up the user's
                organization membership to find their group
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <strong>Check group permissions</strong> - Verify the group has
                the required permission ID with a matching resource pattern
              </Text>
            </List.Item>
          </List>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light" mb="md">
            The <Code>getMyAccount</Code> query returns the current user's
            permissions, which can be used client-side for UI gating (hiding
            buttons, disabling features). The actual authorization is always
            enforced server-side on every request.
          </Alert>

          <Title order={4} mb="xs">
            Default public grants (self-scoped)
          </Title>
          <Text mb="sm" size="sm">
            The platform automatically grants a set of self-scoped permissions
            to every authenticated user through the <strong>CONNECTED</strong>{" "}
            group's default grant, without requiring explicit group assignment.
            These cover common self-service actions. The current set includes:
          </Text>
          <Table striped highlightOnHover className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Permission ID</th>
                <th>Default resource scope</th>
                <th>What it allows</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>organization:account:identity:refresh-token</Code>
                </td>
                <td>
                  <Code>organization:account:{"{user.id}"}:identity:*</Code>
                </td>
                <td>Refresh own identity tokens</td>
              </tr>
              <tr>
                <td>
                  <Code>organization:account:identity:delete</Code>{" "}
                  <Badge color="teal" variant="light" size="xs" ml={4}>
                    New
                  </Badge>
                </td>
                <td>
                  <Code>organization:account:{"{user.id}"}:identity:*</Code>
                </td>
                <td>
                  Remove a linked identity from the caller's own account (the
                  creation identity and the last remaining identity can never
                  be deleted)
                </td>
              </tr>
            </tbody>
          </Table>
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Admins who need to delete <em>another</em> account's identity must
            hold the <Code>organization:account:identity:delete</Code>{" "}
            permission with a resource pattern that matches{" "}
            <Code>
              organization:account:{"{accountId}"}:identity:{"{providerId}"}
            </Code>
            . Grant this through a custom group in IAM &gt; Groups.
          </Alert>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations for the IAM system.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="member-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Member Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationMembers (list)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float) {
  organizationMembers(page: { first: $count, after: $cursor }) {
    nodes {
      memberId
      organizationId
      groupId
      group { id, name, type }
      account { id, email, profiles { username } }
      permissions { id, resources }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      searchPlayer (for member invitation)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Search for players by username to invite them as members.
                    </Text>
                    <GqlBlock>
                      {`query ($username: String!) {
  searchPlayer(username: $username) {
    id
    username
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      getMyAccount (current user permissions)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Returns the authenticated user's account with their
                      effective permissions. Used for client-side permission
                      checks.
                    </Text>
                    <GqlBlock>
                      {`query {
  getMyAccount {
    id
    email
    permissions { id, resources }
    profiles { id, username, avatarUrl }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="member-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Member Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      setOrganizationMembership (create/update)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Upsert a member. Provide either <Code>email</Code> (for
                      console access) or <Code>userId</Code> (for existing
                      users). If the member already exists, their group is
                      updated. <Code>groupId</Code> is optional — omit it to
                      create a member with no group (the default for
                      application/module memberships). For such members,{" "}
                      <Code>groupId</Code> and <Code>group</Code> are returned as{" "}
                      <Code>null</Code>, so consumers must handle the nullable
                      group on <Code>organizationMembers</Code> and{" "}
                      <Code>setOrganizationMembership</Code>.
                    </Text>
                    <GqlBlock>
                      {`mutation ($email: EmailAddress, $userId: ID, $groupId: ID,
         $permissions: [GroupPermissionInput!]) {
  setOrganizationMembership(input: {
    email: $email
    userId: $userId
    groupId: $groupId
    permissions: $permissions
  }) {
    memberId
    groupId
    group { id, name }
  }
}

# GroupPermissionInput:
# { id: String!, resources: [String!]! }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationMembership
                    </Text>
                    <GqlBlock>
                      {`mutation ($userId: ID!) {
  deleteOrganizationMembership(input: { userId: $userId })
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="group-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Group Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationGroups (list)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float) {
  organizationGroups(page: { first: $count, after: $cursor }) {
    nodes {
      id
      name
      organizationId
      type           # ADMIN | ANONYMOUS | CONNECTED | MEMBER
      permissions { id, resources }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationGroup (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  organizationGroup(id: $id) {
    id
    name
    organizationId
    type
    permissions { id, resources }
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      availablePermissions
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Returns all permissions that can be assigned to groups.
                      Used to populate the permission selector when creating or
                      editing groups.
                    </Text>
                    <GqlBlock>
                      {`query {
  availablePermissions {
    organizationPermissions {
      id                    # e.g. "tournament:manage"
      description           # Human-readable description
      availableVariables    # Variables that can be used in resources
      resources             # Default resource patterns
    }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="group-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Group Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createOrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`mutation ($name: String!, $permissions: [GroupPermissionInput!]!) {
  createOrganizationGroup(input: {
    name: $name
    permissions: $permissions
  }) {
    id
    name
    permissions { id, resources }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateOrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $name: String!, $permissions: [GroupPermissionInput!]!) {
  updateOrganizationGroup(id: $id, input: {
    name: $name
    permissions: $permissions
  }) {
    id
    name
    permissions { id, resources }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  deleteOrganizationGroup(id: $id)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="app-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Application Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationApps (list all apps)
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float) {
  organizationApps(page: { first: $count, after: $cursor }) {
    nodes {
      id
      name
      description
      organizationId
      public
      creatorId
      creator { id, email }
      configuration {
        name
        scope
        redirectUrls
        logoutRedirectUrls
        metadata {
          creatorId
          public
          consentUrl
          loginUrl
          requiresConsent
        }
      }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationApp (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: String!) {
  organizationApp(id: $id) {
    id
    name
    description
    organizationId
    public
    secret
    configuration {
      name
      scope
      redirectUrls
      logoutRedirectUrls
      metadata { creatorId, public, consentUrl, loginUrl, requiresConsent }
    }
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationAttachedApps (list attached to org)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Lists all applications attached to the current
                      organization, including their group assignment.
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float) {
  organizationAttachedApps(page: { first: $count, after: $cursor }) {
    nodes {
      appId
      organizationId
      groupId
      app { id, name, description }
      group { id, name, type }
      permissions { id, resources }
      createdAt
      updatedAt
    }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="app-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Application Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreateOrganizationAppInput!) {
  createOrganizationApp(input: $input) {
    id
    name
    secret    # Only returned on creation
  }
}

# CreateOrganizationAppInput:
# {
#   name: String!
#   description: String!
#   public: Boolean
#   secret: String
#   consentUrl: String!
#   loginUrl: String!
#   redirectUrls: [String!]!
#   logoutRedirectUrls: [String!]!
#   requiresConsent: Boolean!
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!, $input: UpdateOrganizationAppInput!) {
  updateOrganizationApp(id: $id, input: $input) {
    id
    name
  }
}

# UpdateOrganizationAppInput:
# {
#   name: String
#   description: String
#   secret: String
#   consentUrl: String!
#   loginUrl: String!
#   redirectUrls: [String!]!
#   logoutRedirectUrls: [String!]!
#   requiresConsent: Boolean!
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!) {
  deleteOrganizationApp(id: $id)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      resetOrganizationAppSecret
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Generates a new secret for the application, invalidating
                      the previous one.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!) {
  resetOrganizationAppSecret(id: $id)
  # Returns: String! (the new secret)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      setOrganizationAttachedApp (attach app to org)
                    </Text>
                    <GqlBlock>
                      {`mutation ($appId: String!, $groupId: ID, $permissions: [GroupPermissionInput!]) {
  setOrganizationAttachedApp(input: {
    appId: $appId
    groupId: $groupId
    permissions: $permissions
  }) {
    appId
    groupId
    group { id, name }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationAttachedApp (detach app)
                    </Text>
                    <GqlBlock>
                      {`mutation ($appId: String!) {
  deleteOrganizationAttachedApp(input: { appId: $appId })
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Membership Inputs
                    </Text>
                    <GqlBlock>
                      {`input SetOrganizationMembershipInput {
  userId: ID                          # For existing users (by player search)
  email: EmailAddress                 # For email invitation
  groupId: ID                         # Group to assign
  permissions: [GroupPermissionInput!] # Optional direct permissions
}

input DeleteOrganizationMembershipInput {
  userId: ID!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Group Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationGroupInput {
  name: String!
  permissions: [GroupPermissionInput!]!
}

input GroupPermissionInput {
  id: String!           # Permission identifier
  resources: [String!]! # Resource patterns (use ["*"] for all)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Application Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationAppInput {
  name: String!
  description: String!
  public: Boolean
  secret: String
  consentUrl: String!
  loginUrl: String!
  redirectUrls: [String!]!
  logoutRedirectUrls: [String!]!
  requiresConsent: Boolean!
}

input UpdateOrganizationAppInput {
  name: String
  description: String
  secret: String
  consentUrl: String!
  loginUrl: String!
  redirectUrls: [String!]!
  logoutRedirectUrls: [String!]!
  requiresConsent: Boolean!
}

input SetOrganizationAttachedAppInput {
  appId: String!
  groupId: ID
  permissions: [GroupPermissionInput!]
}

input DeleteOrganizationAttachedAppInput {
  appId: String!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used in the IAM system. All types are generated
            from the GraphQL schema.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="types">
              <Accordion.Control>Types</Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationMember
                    </Text>
                    <GqlBlock>
                      {`type OrganizationMember {
  memberId: ID!
  organizationId: ID!
  groupId: ID!
  group: OrganizationGroup!
  account: Account!
  permissions: [GroupPermission!]
  createdAt: DateTime!
  updatedAt: DateTime!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationGroup
                    </Text>
                    <GqlBlock>
                      {`type OrganizationGroup {
  id: ID!
  name: String!
  organizationId: ID!
  type: OrganizationGroupType!
  permissions: [GroupPermission!]!
  createdAt: DateTime!
  updatedAt: DateTime!
}

type GroupPermission {
  id: String!           # Permission identifier
  resources: [String!]! # Resource scope patterns
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationApp
                    </Text>
                    <GqlBlock>
                      {`type OrganizationApp {
  id: String!
  name: String!
  description: String!
  organizationId: ID!
  creatorId: ID!
  creator: Account!
  public: Boolean
  secret: String
  configuration: OrganizationAppConfig!
  createdAt: DateTime!
  updatedAt: DateTime!
}

type OrganizationAppConfig {
  name: String!
  organizationId: ID!
  scope: String!
  redirectUrls: [String!]!
  logoutRedirectUrls: [String!]!
  metadata: OrganizationAppConfigMetadata!
}

type OrganizationAppConfigMetadata {
  creatorId: String!
  public: Boolean!
  consentUrl: String!
  loginUrl: String!
  requiresConsent: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationAttachedApp
                    </Text>
                    <GqlBlock>
                      {`type OrganizationAttachedApp {
  appId: String!
  organizationId: ID!
  groupId: ID!
  app: OrganizationApp!
  group: OrganizationGroup!
  permissions: [GroupPermission!]
  createdAt: DateTime!
  updatedAt: DateTime!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Permission
                    </Text>
                    <GqlBlock>
                      {`type Permission {
  id: String!                    # e.g. "tournament:manage"
  description: String!           # Human-readable description
  availableVariables: [String!]! # Variables for resource patterns
  resources: [String!]!          # Default resource patterns
}

type Permissions {
  organizationPermissions: [Permission!]!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OrganizationGroupType
                    </Text>
                    <GqlBlock>
                      {`ADMIN        # Full access, system-managed
ANONYMOUS    # Unauthenticated users
CONNECTED    # Authenticated users
MEMBER       # Custom groups created by admins`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>
      </Stack>
    </div>
  );
};
